import { alea } from 'seedrandom';
import { Images } from '../../../../@resources/img';
import { Arr } from '../../../../CodesForFoundation';
import { uuid } from '../../../../CodesThatsGottaGoSomewhereElse';
import { IsolatedObjectManager } from '../../../../concepts/objects/IsolatedObjectManager';
import { Point } from '../../../../platform/geom/Point';
import { EmojiStore } from '../../../../ui/emoji';
import { Physical } from '../../../canvas/entities/Physical';
import { GridLayout } from '../../../canvas/layout/GridLayout';
import { LineLayout } from '../../../canvas/layout/LineLayout';
import { Pen } from '../../../canvas/layout/Pen';
import { StackLayout } from '../../../canvas/layout/StackLayout';
import { Sticky } from '../../paper/Sticky';
import { Zone } from '../../zones/Zone';
import {
    MappingFilter,
    MappingTarget,
    TabularData,
    TabularDataDimension,
    TabularImportParams,
    TabularImportStrategy
} from '../TabularImportModel';
import { TabularImportGroupingStep } from '../steps/TabularImportGroupingStep';
import { TabularImportMappingStep } from '../steps/TabularImportMappingStep';
import { TabularImportStepPreviewGenerator } from '../steps/TabularImportStepPreview';

const selectEmojiForKey = (key: string) => {
    const rand = alea(key, { global: true });
    const emoji = EmojiStore.getEmoji();
    return emoji[Math.floor(rand() * emoji.length)].id;
};

const previewStickiesInZones: TabularImportStepPreviewGenerator = (data, params, page) => {

    const grouping = params.grouping as TabularDataDimension;
    if (!grouping) return { figures: [], pages: 0 };

    const items = params.hasHeaders ? data.slice(1) : data;
    const grouped = Arr.groupBy(items, grouping.key);
    const keys = Object.keys(grouped);
    const counts = Object.fromEntries(Object.entries(grouped).map(x => [x[0], x[1].length]));

    const selected = Arr.at(keys, page);

    const iom = new IsolatedObjectManager();
    const figures = [] as Physical[];

    const zone = iom.create(Zone);
    zone.x = 0;
    zone.y = 0;
    zone.width = 640;
    zone.height = 480;
    zone.title = selected;
    zone.emoji = selectEmojiForKey(selected);
    figures.push(zone);

    const sticky = iom.create(Sticky);
    sticky.moveBy(50, 100);
    sticky.content = `${counts[selected]} stickies in this group.`;
    zone.add(sticky);
    figures.push(sticky);

    return { figures, pages: keys.length };
};

const composeStickiesInZones = (data: TabularData, params: TabularImportParams) => {
    const { mappings, grouping } = params;

    const iom = new IsolatedObjectManager();
    const items = params.hasHeaders ? data.slice(1) : data;
    const grouped = Arr.groupBy(items, grouping.key);
    const keys = Object.keys(grouped);

    // Produce the stickies (indexed by the source item)
    const stickies = new Map(items.map(tm => {
        const sk = iom.create(Sticky);
        for (const [t, s] of mappings) {
            sk.set(t.path, t.filter(tm[s.key]));
        }
        return [tm, sk];
    }));

    // Produce the zones (indexed by their key)
    const zones = new Map(keys.map(ky => {
        const zn = iom.create(Zone);
        zn.title = ky;
        zn.emoji = selectEmojiForKey(ky);
        return [ky, zn];
    }));

    // Find the group with the most stickies and calculate the grid area of the stickies
    const [largest] = Arr.max(Object.entries(grouped), ((a, b) => b[1].length - a[1].length));
    const area = GridLayout.compute(Pen.from(grouped[largest].map(tm => stickies.get(tm)))).bounds();

    // Perform sizing of zones and layout of contents using the largest height as the common value
    for (const [ky, zn] of zones) {
        const sks = grouped[ky].map(tm => stickies.get(tm));
        const pts = StackLayout.computeLtr(Point.zero, area.height, Pen.from(sks)).moveBy(40, 80).apply();
        zn.width = Math.max(pts.bounds().width, zn.minWidth) + 80;
        zn.height = area.height + 120;
        sks.forEach(x => zn.add(x));
    }

    // Finally layout the zones
    LineLayout.computeHorizontal(Pen.from(zones.values())).centerize().apply();

    return Array.from(zones.values()).map(x => x.toTuple());
};

export const StickiesInZonesTabularImportStrategy: TabularImportStrategy = {
    id: uuid(),
    label: 'Import as Stickies In Zones',
    image: Images.ImportStickiesInZones,
    steps: [
        TabularImportMappingStep({
            type: Sticky,
            typeLabel: 'Stickies',
            preview: [2, 4],
            targets: [
                MappingTarget('Content', 'content', MappingFilter.text(140)),
                MappingTarget('Color', 'background', MappingFilter.color),
            ]
        }),
        TabularImportGroupingStep({
            label: 'Stickies',
            preview: previewStickiesInZones,
        }),
    ],
    apply: composeStickiesInZones,
};
